import { useMemo, useEffect } from 'react'; // Added useEffect
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";

// ... JSDoc ...
export function useUserRole() {
    const doctorId = localStorage.getItem('user_id');

    // Read cached state to prevent flicker
    const cachedIsReceptionist = localStorage.getItem('role_is_receptionist');
    // Default to false (Doctor) if no cache, as that's the primary use case and avoids hiding Dashboard for doctors strictly on load
    const initialIsReceptionist = cachedIsReceptionist === 'true';

    // Remove || [] to detect loading state (undefined)
    const memberships = useLiveQuery(async () => {
        if (!doctorId) return [];
        return db.clinic_members
            .where('user_id').equals(doctorId)
            .filter(m => m.is_active !== false)
            .toArray();
    }, [doctorId]);

    const clinics = useLiveQuery(async () => {
        if (!memberships || memberships.length === 0) return [];
        const clinicIds = memberships.map(m => m.clinic_id).filter(Boolean);
        if (clinicIds.length === 0) return [];
        return db.clinics.where('id').anyOf(clinicIds).toArray();
    }, [memberships]);

    const roleInfo = useMemo(() => {
        if (!memberships || clinics === undefined) {
            return {
                isLoading: true,
                isReceptionistOnly: initialIsReceptionist, // Use cached value
                hasAdminRole: false,
                hasDoctorRole: false,
                hasConsultantRole: false,
                hasReceptionistRole: false,
                hasFinancialAccess: false,
                showSimplifiedDashboard: initialIsReceptionist,
                hasAnyClinic: false,
                memberships: [],
                clinics: [],
                user: doctorId ? { id: doctorId } : null,
                getRoleForClinic: () => null
            };
        }

        const hasAdminRole = memberships.some(m => m.role === 'admin');
        const hasDoctorRole = memberships.some(m => m.role === 'doctor');
        const hasConsultantRole = memberships.some(m => m.role === 'consultant');
        const hasReceptionistRole = memberships.some(m => m.role === 'receptionist');
        const hasAnyClinic = memberships.length > 0;

        // User is "receptionist only" if they have memberships but none are admin/doctor/consultant
        const isReceptionistOnly = hasAnyClinic && !hasAdminRole && !hasDoctorRole && !hasConsultantRole;

        const hasFinancialAccess = hasAdminRole || hasConsultantRole;
        const showSimplifiedDashboard = hasAnyClinic && !hasAdminRole && !hasConsultantRole;

        // Function to get role for a specific clinic
        const getRoleForClinic = (clinicId) => {
            if (!clinicId) return null;
            const membership = memberships.find(m => m.clinic_id === clinicId);
            return membership?.role || null;
        };

        return {
            isLoading: false,
            isReceptionistOnly,
            hasAdminRole,
            hasDoctorRole,
            hasConsultantRole,
            hasReceptionistRole,
            hasFinancialAccess,
            showSimplifiedDashboard,
            hasAnyClinic,
            memberships,
            clinics: clinics || [],
            user: doctorId ? { id: doctorId } : null,
            getRoleForClinic
        };
    }, [memberships, clinics, initialIsReceptionist, doctorId]);

    // Cache the result when strictly computed
    useEffect(() => {
        if (!roleInfo.isLoading) {
            localStorage.setItem('role_is_receptionist', roleInfo.isReceptionistOnly);
        }
    }, [roleInfo.isLoading, roleInfo.isReceptionistOnly]);

    return roleInfo;
}
